import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './Whatsapp.css';
import { toast } from 'react-toastify';
import AttachmentPreview from './attachment/AttachmentPreview';
import services from '../services';

const WhatsApps = ({
    onSendMessage,
    chatData,
    profileId,
    getChatById,
    selectedMail,
    chatContainerRef,
    isSocketConnected,
    sendingMessages = new Set(),
    taskData,
    onContactSelect,
    selectedContact,
    selectedImages,
    setSelectedImages,
    selectedGroupContacts,
    loading,
    handleScroll
}) => {
    const [message, setMessage] = useState('');
    const [isTemplateActive, setIsTemplateActive] = useState(false);

    const [previewImage, setPreviewImage] = useState(null);
    const [showDialog, setShowDialog] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [allTemp, setAllTemp] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [dialogPosition, setDialogPosition] = useState({ top: 0, left: 0 });
    const [hoveredTemplate, setHoveredTemplate] = useState(null);
    const [savedRange, setSavedRange] = useState(null);
    const dialogRef = useRef(null);
    const inputRef = useRef(null);

    const getTemplate = async () => {
        try {
            const response = await services.template.GET_ALL_TEMPLATE();
            if (response && Array.isArray(response.templates) && response.templates.length > 0) {
                const filteredTemplates = response.templates.filter(item => item.status === true);
                const mappedTemplates = filteredTemplates.map(item => ({
                    label: item.name || '',
                    value: item.id || '',
                    body: item.body || item.value || ''
                }));
                setAllTemp(mappedTemplates);
                setFilteredList(mappedTemplates);
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        getTemplate();
    }, []);

    useEffect(() => {
        setFilteredList(
            allTemp.filter(item =>
                item.label.toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    }, [searchTerm, allTemp]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dialogRef.current && !dialogRef.current.contains(event.target) && inputRef.current && !inputRef.current.contains(event.target)) {
                setShowDialog(false);
            }
        };
        if (showDialog) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDialog]);

    const handleInputChange = (e) => {
        setMessage(e.target.value);
    };

    const handleKeyUp = (e) => {
        const selection = window.getSelection();
        if (!selection.rangeCount) return;

        const range = selection.getRangeAt(0);
        const textBeforeCursor = range.startContainer.textContent?.substring(0, range.startOffset) || "";

        if (textBeforeCursor.endsWith('**') && allTemp?.length > 0) {
            const rect = range.getBoundingClientRect();
            setDialogPosition({
                top: rect.top - 260, // Show above the input
                left: rect.left
            });
            setSavedRange(range.cloneRange());
            setSearchTerm('');
            setShowDialog(true);
        }

        if (e.key === 'Escape') {
            setShowDialog(false);
        }
    };

    const handleTemplateSelect = (item) => {
        let range = savedRange;
        const selection = window.getSelection();

        if (!range) {
            if (selection.rangeCount > 0) {
                range = selection.getRangeAt(0);
            } else {
                return;
            }
        }

        // Find the "**" text node and remove it
        const startNode = range.startContainer;
        if (startNode.nodeType === Node.TEXT_NODE) {
            const text = startNode.textContent;
            const lastIndex = text.lastIndexOf('**', range.startOffset);
            if (lastIndex !== -1) {
                startNode.textContent = text.substring(0, lastIndex) + text.substring(range.startOffset);
                range.setStart(startNode, lastIndex);
                range.setEnd(startNode, lastIndex);
            }
        }

        // Create a temporary element to hold the template HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = item.body;

        // Insert all nodes from the template
        const fragment = document.createDocumentFragment();
        let lastNode;
        while (tempDiv.firstChild) {
            lastNode = tempDiv.firstChild;
            fragment.appendChild(lastNode);
        }

        range.deleteContents(); // Clear the range (removes the '**' if it was selected or just clears the point)
        range.insertNode(fragment);

        // Move cursor to the end of inserted content
        if (lastNode) {
            range.setStartAfter(lastNode);
            range.setEndAfter(lastNode);
            selection.removeAllRanges();
            selection.addRange(range);
        }

        setMessage(inputRef.current.innerHTML);
        setIsTemplateActive(true);
        setShowDialog(false);
        setSearchTerm('');
        setSavedRange(null);
        inputRef.current.focus();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const content = inputRef.current.innerHTML;
        if (content.trim() !== '' && content !== '<br>' || selectedImages.length > 0) {
            onSendMessage(content);
            setMessage('');
            setIsTemplateActive(false);
            inputRef.current.innerHTML = '';
            setSelectedImages([]);
        }
    };
    const convertEpochToTime = (epoch) => {
        const date = new Date(epoch * 1000);
        return date.toLocaleTimeString('en-US', { hour12: false });
    };

    const colors = [
        '#1e88e5',
        '#e53935',
        '#8e24aa',
        '#43a047',
        '#fb8c00',
        '#00acc1',
        '#6d4c41',
        '#f4511e',
        '#3949ab',
        '#00897b',
    ];

    function getColorFromName(name = '') {
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        const index = Math.abs(hash) % colors.length;
        return colors[index];
    }

    const handleRemoveImage = (idx) => {
        setSelectedImages((prev) => prev.filter((_, i) => i !== idx));
    };

    const MAX_IMAGES = 4;

    const handleFileChange = (e) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            if (selectedImages.length + newFiles.length > MAX_IMAGES) {
                toast.error(`You can only select up to ${MAX_IMAGES} images.`);
                return;
            }
            setSelectedImages((prev) => [...prev, ...newFiles]);
        }
    };

    const headerName = selectedGroupContacts?.label || selectedContact?.name || selectedContact?.contactNo || 'Select a chat';



    return (
        <div className="flex h-screen  font-sans">
            <style>{`
                .custom-dialog {
                    position: fixed;
                    max-height: 400px;
                    background: white;
                    border: 1px solid #ccc;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.25);
                    border-radius: 12px;
                    z-index: 10000;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }
                .dialog-header {
                    border-bottom: 1px solid #eee;
                    background: #f8f9fa;
                }
                .dialog-content {
                    display: flex;
                    flex: 1;
                    overflow: hidden;
                }
                .custom-dialog input {
                    box-sizing: border-box;
                    width: 100%;
                    padding: 12px 16px;
                    border: none;
                    outline: none;
                    margin: 0;
                    font-size: 15px;
                    background: transparent;
                }
                
                .template-list {
                    border-right: 1px solid #eee;
                    overflow-y: auto;
                    list-style: none;
                    margin: 0;
                    padding: 0;
                }
                
                .template-list li {
                    padding: 12px 16px;
                    cursor: pointer;
                    font-size: 14px;
                    color: #333;
                    border-bottom: 1px solid #f9f9f9;
                    transition: all 0.2s;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .template-list li.active, .template-list li:hover {
                    background-color: #f0f7f4;
                    color: #00A884;
                    font-weight: 500;
                }
                .preview-placeholder {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    height: 100%;
                    color: #999;
                    font-style: italic;
                    text-align: center;
                }
                .preview-html-content {
                    color: #333;
                    line-height: 1.5;
                }
                .preview-html-content img {
                    max-width: 100%;
                    height: auto;
                }
            `}</style>
            {showDialog && (
                <div
                    ref={dialogRef}
                    className="custom-dialog"
                    style={{ top: dialogPosition.top, left: dialogPosition.left }}
                >
                    <div className="dialog-header">
                        <input
                            autoFocus
                            type="text"
                            placeholder="Search templates..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') setShowDialog(false);
                            }}
                        />
                    </div>
                    <div className="dialog-content">
                        <ul className="template-list">
                            {filteredList.length > 0 ? (
                                filteredList.map((item, index) => (
                                    <li
                                        key={index}
                                        onClick={() => handleTemplateSelect(item)}
                                        onMouseEnter={() => setHoveredTemplate(item)}
                                        className={hoveredTemplate?.value === item.value ? 'active' : ''}
                                    >
                                        {item.label}
                                    </li>
                                ))
                            ) : (
                                <li style={{ color: '#999', cursor: 'default' }}>
                                    No templates found
                                </li>
                            )}
                        </ul>

                    </div>
                </div>
            )}
            {previewImage && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        background: 'rgba(0,0,0,0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                    }}
                    onClick={() => setPreviewImage(null)}
                >
                    <div
                        style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={previewImage}
                            alt="Preview"
                            style={{
                                maxWidth: '90vw',
                                maxHeight: '90vh',
                                borderRadius: 12,
                                boxShadow: '0 2px 16px rgba(0,0,0,0.3)',
                            }}
                        />
                        <button
                            onClick={() => setPreviewImage(null)}
                            style={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                background: 'rgba(0,0,0,0.6)',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '50%',
                                width: 32,
                                height: 32,
                                fontSize: 20,
                                cursor: 'pointer',
                            }}
                            aria-label="Close preview"
                        >
                            &times;
                        </button>
                    </div>
                </div>
            )}

            <div className="flex-1 flex flex-col px-[2vw]">
                <section className="msger flex flex-col h-full">

                    <header className="msger-header sticky top-0 z-10 ">

                        <div className="msger-header-title flex flex-col justify-center items-center gap-2">
                            <div className="flex items-center gap-2">
                                <div
                                    className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-600"
                                >
                                    {
                                        selectedContact?.profileImage && selectedContact?.profileImage.trim() !== ""
                                            ? (
                                                <img
                                                    src={`${process.env.REACT_APP_IMAGE_URL}/${selectedContact?.profileImage}`}
                                                    alt="profile"
                                                    className="w-full h-full rounded-full object-cover"
                                                    onError={e => { e.target.onerror = null; e.target.src = "/default-profile.png"; }}
                                                />
                                            )
                                            : (
                                                <i className="fas fa-user text-xl"></i>
                                            )
                                    }
                                </div>
                                {headerName}
                            </div>
                        </div>
                        <div className="msger-header-options flex items-center gap-2">
                            <div
                                className="flex items-center gap-1 text-xs"
                                style={{ color: isSocketConnected ? '#4CAF50' : '#f44336' }}
                                title={isSocketConnected ? 'Socket Connected' : 'Socket Disconnected'}
                            >
                                <span
                                    className="inline-block rounded-full"
                                    style={{
                                        width: '8px',
                                        height: '8px',
                                        backgroundColor: isSocketConnected ? '#4CAF50' : '#f44336',
                                    }}
                                ></span>
                                {isSocketConnected ? 'Connected' : 'Disconnected'}
                            </div>

                        </div>
                    </header>

                    <main className="msger-chat flex-1 overflow-y-auto mb-[80px]" ref={chatContainerRef}  >
                        {!selectedContact && !selectedGroupContacts?.value ? (
                            <div className="flex items-center justify-center h-full text-[#8696a0] text-lg">

                            </div>
                        ) : loading ? (
                            <div className="flex items-center justify-center h-full text-[#8696a0] text-lg">
                                <div className="flex items-center gap-2">
                                    <span className="loader w-5 h-5 border-2 border-t-transparent rounded-full animate-spin border-[#8696a0]"></span>
                                    <span>Loading chat...</span>
                                </div>
                            </div>
                        ) : chatData && chatData?.length > 0 ? (
                            chatData?.filter((item) => item?.
                                attachementPath?.length > 0 || item?.message).map((data, index) => (
                                    <div
                                        key={data?.id || index}

                                        className={
                                            data?.messageSendBy?.split('@')[0] === profileId?.split('@')[0]
                                                ? 'msg left-msg'
                                                : 'msg right-msg'
                                        }
                                    >
                                        <div
                                            className="msg-img"
                                            style={{
                                                backgroundImage: `url(${data?.messageSendBy === profileId
                                                    ? 'https://image.flaticon.com/icons/svg/145/145867.svg'
                                                    : 'https://image.flaticon.com/icons/svg/327/327779.svg'
                                                    })`,
                                            }}
                                        />

                                        <div>
                                            <div className="msg-bubble">
                                                <div className="msg-info">
                                                    {data?.chatType === 'group' && (
                                                        <div className="msg-info-name">Sajad</div>
                                                    )}
                                                    <div
                                                        className="msg-info-time"
                                                        style={{ color: getColorFromName(data?.participantName || '') }}
                                                    >
                                                        <span>{data?.participantName}</span>
                                                    </div>
                                                </div>


                                                <div className="msg-text d-flex flex-col">
                                                    <div>
                                                        <AttachmentPreview data={data} />
                                                    </div>

                                                    <div className="d-flex items-center gap-2 justify-between">
                                                        <span className="fs-12 break-words max-w-[85%]" style={{ marginBottom: '0' }}>
                                                            {typeof data?.message === 'string'
                                                                ? data.message
                                                                : typeof data?.message?.body === 'string'
                                                                    ? data.message.body
                                                                    : JSON.stringify(data?.message)}
                                                        </span>

                                                        <span>
                                                            {data?.lastSeen != null && data.lastSeen !== ""
                                                                ? convertEpochToTime(data.lastSeen)
                                                                : ""}
                                                        </span>


                                                        {sendingMessages.has(data?.id) && (
                                                            <span className="text-muted text-xs">
                                                                <i className="fas fa-clock"></i> Sending...
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>


                                        </div>
                                    </div>
                                ))
                        ) : (
                            <div className="flex items-center justify-center h-full text-[#8696a0] text-lg">
                                No messages yet. Start the conversation!
                            </div>
                        )}
                    </main>


                    <div className="sticky bottom-0 z-10 bg-[#e5ddd5] px-[1vw] py-[1vw]">
                        <form className="msger-inputarea rounded-full px-[1.5vw] gap-[1vw] flex items-center" onSubmit={handleSubmit}>
                            <input
                                type="file"
                                id="attachment-input"
                                className="hidden"
                                onChange={handleFileChange}
                                multiple
                                accept="image/*"
                            />

                            <label htmlFor="attachment-input" className="cursor-pointer mr-2" title="Attach file">
                                <i className="fas fa-paperclip text-lg"></i>
                            </label>

                            <div
                                ref={inputRef}
                                contentEditable={(selectedContact || selectedGroupContacts?.value) ? true : false}
                                className="msger-input flex-1 px-[1vw] py-[0.5vw] rounded bg-white text-dark border-none outline-none overflow-y-auto whitespace-pre-wrap text-left"
                                style={{
                                    minHeight: isTemplateActive ? '150px' : '60px',
                                    maxHeight: isTemplateActive ? '450px' : '150px',
                                    borderRadius: isTemplateActive ? '35px' : '15px'
                                }}
                                onInput={(e) => {
                                    if (isTemplateActive) {
                                        const content = e.currentTarget.innerHTML;
                                        if (content.trim() === '' || content === '<br>') {
                                            setIsTemplateActive(false);
                                        }
                                    }
                                }}
                                onKeyUp={handleKeyUp}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSubmit(e);
                                    }
                                }}
                                data-placeholder="Enter your message..."
                            />
                            <style>{`
                                .msger-input:empty:before {
                                    content: attr(data-placeholder);
                                    color: #8696a0;
                                }
                            `}</style>

                            <div className='flex items-center gap-[1vw]'>
                                {selectedImages?.map((file, idx) => (
                                    <div
                                        key={idx}
                                        className="preview-container"
                                        style={{
                                            position: 'relative',
                                            width: 100,
                                            height: 100,
                                            display: 'inline-block',
                                            marginBottom: 12,
                                            background: '#fff',
                                            boxSizing: 'border-box',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {file.type.startsWith('image/') ? (
                                            <img
                                                src={URL.createObjectURL(file)}
                                                alt={`Preview ${idx}`}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    borderRadius: 8,
                                                    boxShadow:
                                                        'rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px',
                                                }}
                                            />
                                        ) : (
                                            <div
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexDirection: 'column',
                                                    fontSize: 32,
                                                    color: '#2196f3',
                                                    background: '#fafafa',
                                                    borderRadius: 8,
                                                }}
                                            >
                                                {file.name.endsWith('.xls') || file.name.endsWith('.xlsx') ? (
                                                    <span role="img" aria-label="xls">
                                                        📄<div style={{ fontSize: 12, color: '#888' }}>XLS</div>
                                                    </span>
                                                ) : file.name.endsWith('.pdf') ? (
                                                    <span role="img" aria-label="pdf">
                                                        📄<div style={{ fontSize: 12, color: '#888' }}>PDF</div>
                                                    </span>
                                                ) : (
                                                    <span role="img" aria-label="file">
                                                        📄<div style={{ fontSize: 12, color: '#888' }}>FILE</div>
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            className="remove-image-btn"
                                            onClick={() => handleRemoveImage(idx)}
                                            title="Remove"
                                        >
                                            &times;
                                        </button>
                                    </div>

                                ))}
                            </div>

                            <button
                                type="submit"
                                className={`w-[3vw] h-[3vw] p-[0.6vw] rounded-full flex items-center justify-center shadow-md transition-colors duration-200 ${selectedContact || selectedGroupContacts?.value
                                    ? 'bg-[#00A884] hover:bg-[#029174] text-white'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    }`}

                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-6 h-6"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
                                </svg>
                            </button>
                        </form>
                    </div>

                </section>
            </div>
        </div>
    );
};

WhatsApps.propTypes = {
    onSendMessage: PropTypes.func.isRequired,
    chatData: PropTypes.array.isRequired,
    profileId: PropTypes.string,
    getChatById: PropTypes.func.isRequired,
    selectedMail: PropTypes.string,
    chatContainerRef: PropTypes.object,
    isSocketConnected: PropTypes.bool,
    sendingMessages: PropTypes.instanceOf(Set),
    onContactSelect: PropTypes.func,
    selectedContact: PropTypes.object,
};

export default WhatsApps;
